import { createSelector } from 'reselect';

import { dataDaiDefaultValue } from '../../domain/dai/defaultValue';
import { UnitDataListSettingDateRangeParams } from '../../domain/dai/types';
import { UnitDataListFormConditions } from '../../domain/dai/types';
import { SharedUser } from '../../domain/favorites';
import { Option, OrderType } from '../../domain/schemas';
import { ShuOption } from '../../domain/shu';
import {
  DTMA_RANKING_DEFAULT_ORDER,
  DTMA_RANKING_DEFAULT_SORT,
  UnitDataListDataViewMode,
} from '../../domain/unitDataList/ranking';
import {
  DataUnitDataListTransitionSearchParams,
  UnitDataListTransitionSearchParams,
} from '../../domain/unitDataList/types';

import { RootState } from '../../store';
import { getYmdList } from '../../utils/date';
import { KiAlignedYmdSlideLock } from '../../utils/makeKiAlignedSlideDates';
import { ReportsDateRange } from '../../utils/reportsDateRange';
import { favoritesSelector } from './settingsFavorites';

/**
 * Action Types
 *
 * プレフィックスの意味
 * SEARCH: ReducerでStateの変更はしないがデータを取得する（検索ボタン等Sagaを発火させたい）
 * CHANGE: Stateを変更してデータも取得する
 * SELECT: Stateを変更する（データ取得は行わない）
 * RESET: Stateを初期状態に戻す（データ取得は行わない）
 * TRIGGER: Stateを変更する（Sagaを発火させない。フロントソート等）
 */

const SEARCH_UNIT_DATA_LIST_REPORTS = 'SEARCH_UNIT_DATA_LIST_REPORTS' as const;
const SEARCH_RESET_UNIT_DATA_LIST_REPORTS =
  'SEARCH_RESET_UNIT_DATA_LIST_REPORTS' as const;
const SEARCH_UNIT_DATA_LIST_REPORTS_MARKING =
  'SEARCH_UNIT_DATA_LIST_REPORTS_MARKING' as const;
const SEARCH_UNIT_DATA_LIST_REPORTS_FIELDS =
  'SEARCH_UNIT_DATA_LIST_REPORTS_FIELDS' as const;

const CHANGE_UNIT_DATA_LIST_REPORTS_FAVORITE =
  'CHANGE_UNIT_DATA_LIST_REPORTS_FAVORITE' as const;
const CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_SHU =
  'CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_SHU' as const;
const CHANGE_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE =
  'CHANGE_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE' as const;
// 表示区分を状態のみ更新する（データ再取得・ソート退避を伴わない）。お気に入り適用やURL同期で使用する
const SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE =
  'SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE' as const;
// 表示区分をURLから確定済みとして記録する（URL同期の初期化が済んだことの目印）
const SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE_RESOLVED_FROM_URL =
  'SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE_RESOLVED_FROM_URL' as const;

const SELECT_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION =
  'SELECT_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION' as const;
const SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_SHU =
  'SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_SHU' as const;
const SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS =
  'SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS' as const;
const SELECT_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS =
  'SELECT_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS' as const;

const RESET_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION =
  'RESET_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION' as const;
const RESET_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS =
  'RESET_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS' as const;

const SAVE_UNIT_DATA_LIST_REPORTS_FAVORITE =
  'SAVE_UNIT_DATA_LIST_REPORTS_FAVORITE' as const;
const SAVE_AS_UNIT_DATA_LIST_REPORTS_FAVORITE =
  'SAVE_AS_UNIT_DATA_LIST_REPORTS_FAVORITE' as const;

const ADD_UNIT_DATA_LIST_REPORTS_TABLE_FILTER =
  'ADD_UNIT_DATA_LIST_REPORTS_TABLE_FILTER' as const;
const REMOVE_UNIT_DATA_LIST_REPORTS_TABLE_FILTER =
  'REMOVE_UNIT_DATA_LIST_REPORTS_TABLE_FILTER' as const;
const APPLY_UNIT_DATA_LIST_REPORTS_TABLE_FILTER =
  'APPLY_UNIT_DATA_LIST_REPORTS_TABLE_FILTER' as const;
const RESET_UNIT_DATA_LIST_REPORTS_TABLE_FILTER =
  'RESET_UNIT_DATA_LIST_REPORTS_TABLE_FILTER' as const;

const SELECT_UNIT_DATA_LIST_REPORTS_MODEL_NAME_FILTER =
  'SELECT_UNIT_DATA_LIST_REPORTS_MODEL_NAME_FILTER' as const;

const SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  'SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER' as const;
const SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS =
  'SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS' as const;

const SELECT_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER =
  'SELECT_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER' as const;
const RESET_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER =
  'RESET_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER' as const;

const SEARCH_UNIT_DATA_LIST_REPORTS_DATE_RANGE_SLIDE =
  'SEARCH_UNIT_DATA_LIST_REPORTS_DATE_RANGE_SLIDE' as const;

const RESET_UNIT_DATA_LIST_REPORTS_FAVORITE_TO_DEFAULT =
  'RESET_UNIT_DATA_LIST_REPORTS_FAVORITE_TO_DEFAULT' as const;

const CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS =
  'CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS' as const;

const CREATE_UNIT_DATA_LIST_REPORTS_SHORTENED_URL =
  'CREATE_UNIT_DATA_LIST_REPORTS_SHORTENED_URL' as const;

const SEARCH_UNIT_DATA_LIST_TRANSITION =
  'SEARCH_UNIT_DATA_LIST_TRANSITION' as const;

const SELECT_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS =
  'SELECT_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS' as const;

const CHANGE_UNIT_DATA_LIST_TRANSITION_SORT =
  'CHANGE_UNIT_DATA_LIST_TRANSITION_SORT' as const;

const SELECT_UNIT_DATA_LIST_TRANSITION_COLUMNS_ORDER =
  'SELECT_UNIT_DATA_LIST_TRANSITION_COLUMNS_ORDER' as const;

const CHANGE_UNIT_DATA_LIST_TRANSITION_FIELDS =
  'CHANGE_UNIT_DATA_LIST_TRANSITION_FIELDS' as const;

const TRIGGER_UNIT_DATA_LIST_TRANSITION_SWAP_FIELDS =
  'TRIGGER_UNIT_DATA_LIST_TRANSITION_SWAP_FIELDS' as const;

const CHANGE_UNIT_DATA_LIST_TRANSITION_DAI_BAN =
  'CHANGE_UNIT_DATA_LIST_TRANSITION_DAI_BAN' as const;

const CHANGE_UNIT_DATA_LIST_TRANSITION_DATE_RANGE =
  'CHANGE_UNIT_DATA_LIST_TRANSITION_DATE_RANGE' as const;

const TRIGGER_UNIT_DATA_LIST_TRANSITION_SORT =
  'TRIGGER_UNIT_DATA_LIST_TRANSITION_SORT' as const;

const TRIGGER_UNIT_DATA_LIST_REPORTS_SORT =
  'TRIGGER_UNIT_DATA_LIST_REPORTS_SORT' as const;

const SELECT_UNIT_DATA_LIST_TRANSITION_IS_HEATMAP_ENABLED =
  'SELECT_UNIT_DATA_LIST_TRANSITION_IS_HEATMAP_ENABLED' as const;

const SELECT_UNIT_DATA_LIST_TRANSITION_IS_TABLE_ANIMATION_ENABLED =
  'SELECT_UNIT_DATA_LIST_TRANSITION_IS_TABLE_ANIMATION_ENABLED' as const;

const RESET_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS =
  'RESET_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS' as const;

const CLEAR_UNIT_DATA_LIST_TRANSITION_DAI_BAN =
  'CLEAR_UNIT_DATA_LIST_TRANSITION_DAI_BAN' as const;

export const UnitDataListReportsSettingActionTypes = {
  SEARCH_UNIT_DATA_LIST_REPORTS,
  SEARCH_RESET_UNIT_DATA_LIST_REPORTS,
  SEARCH_UNIT_DATA_LIST_REPORTS_MARKING,
  SEARCH_UNIT_DATA_LIST_REPORTS_FIELDS,
  CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_SHU,
  CHANGE_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE,
  SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE,
  CHANGE_UNIT_DATA_LIST_REPORTS_FAVORITE,
  SELECT_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION,
  SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_SHU,
  SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS,
  SELECT_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS,
  RESET_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION,
  RESET_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS,
  RESET_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER,
  SAVE_UNIT_DATA_LIST_REPORTS_FAVORITE,
  SAVE_AS_UNIT_DATA_LIST_REPORTS_FAVORITE,
  ADD_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
  REMOVE_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
  APPLY_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
  RESET_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
  SELECT_UNIT_DATA_LIST_REPORTS_MODEL_NAME_FILTER,
  SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
  SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS,
  SELECT_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER,
  SEARCH_UNIT_DATA_LIST_REPORTS_DATE_RANGE_SLIDE,
  RESET_UNIT_DATA_LIST_REPORTS_FAVORITE_TO_DEFAULT,
  CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS,
  CREATE_UNIT_DATA_LIST_REPORTS_SHORTENED_URL,
  SEARCH_UNIT_DATA_LIST_TRANSITION,
  SELECT_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS,
  CHANGE_UNIT_DATA_LIST_TRANSITION_SORT,
  SELECT_UNIT_DATA_LIST_TRANSITION_COLUMNS_ORDER,
  CHANGE_UNIT_DATA_LIST_TRANSITION_FIELDS,
  TRIGGER_UNIT_DATA_LIST_TRANSITION_SWAP_FIELDS,
  CHANGE_UNIT_DATA_LIST_TRANSITION_DAI_BAN,
  CHANGE_UNIT_DATA_LIST_TRANSITION_DATE_RANGE,
  TRIGGER_UNIT_DATA_LIST_TRANSITION_SORT,
  TRIGGER_UNIT_DATA_LIST_REPORTS_SORT,
  SELECT_UNIT_DATA_LIST_TRANSITION_IS_HEATMAP_ENABLED,
  SELECT_UNIT_DATA_LIST_TRANSITION_IS_TABLE_ANIMATION_ENABLED,
  RESET_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS,
  CLEAR_UNIT_DATA_LIST_TRANSITION_DAI_BAN,
};

/**
 * Action Creators
 */

/**
 * 指定した検索条件でデータを取得する（検索ボタン押下時）
 * @param params 検索条件
 * @param dateRange 期間
 * @param dateUnit 日付単位
 * @param isComparison 比較期間の有無
 */
export function searchUnitDataListReportsAction(
  params: UnitDataListFormConditions,
  dateRange: UnitDataListSettingDateRangeParams['dateRange'],
  dateUnit: UnitDataListSettingDateRangeParams['dateUnit'],
  isComparison: UnitDataListSettingDateRangeParams['isComparison'],
  options?: { preserveCurrentHalls?: boolean; shouldUseSlideDates?: boolean }
) {
  return {
    type: SEARCH_UNIT_DATA_LIST_REPORTS,
    payload: {
      params,
      dateRange,
      dateUnit,
      isComparison,
      preserveCurrentHalls: options?.preserveCurrentHalls ?? false,
      /**
       * 期間スライドで今週・今月の日付を機種集計と同じ計算で確定済みのとき true。
       * 検索 Saga ではテンプレ再計算をせず、params の期間を使う。
       */
      shouldUseSlideDates: options?.shouldUseSlideDates ?? false,
    },
  };
}

/**
 * 初期条件でデータを取得する（リセットボタン押下時）
 * @param halls 店舗コード（未指定で全店舗）
 */
function searchResetUnitDataListReportsAction(halls?: string[]) {
  return {
    type: SEARCH_RESET_UNIT_DATA_LIST_REPORTS,
    payload: { halls },
  };
}

/**
 * テーブルヘッダー・サブメニューからのソート（フロントエンド処理）
 * @param sort ソートする条件
 * @param order 昇順・降順
 */
function triggerUnitDataListReportsSortAction(sort: string, order: OrderType) {
  return {
    type: TRIGGER_UNIT_DATA_LIST_REPORTS_SORT,
    payload: { sort, order },
  };
}

/**
 * 指定した表示項目に絞ってデータを再取得する
 * @param fields 表示項目
 * @param fieldsOrder 表示項目パターン適用時の列順（省略時は既存ロジック）
 */
export function searchUnitDataListReportsFieldAction(
  fields: Option[],
  fieldsOrder?: string[]
) {
  return {
    type: SEARCH_UNIT_DATA_LIST_REPORTS_FIELDS,
    payload: { fields, fieldsOrder },
  };
}

/**
 * 選択した種別のデータを取得する
 *
 * MEMO: 初回取得時とテーブル上部の種別選択で変更時に使用する
 * @param shu 絞り込む種別
 */
function changeUnitDataListReportsCurrentShuAction(shu?: ShuOption) {
  return {
    type: CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_SHU,
    payload: { shu },
  };
}

/**
 * 表示区分（標準/出玉ランキング）を変更してデータを取得する
 * @param dataViewMode 表示区分
 */
function changeUnitDataListReportsDataViewModeAction(
  dataViewMode: UnitDataListDataViewMode
) {
  return {
    type: CHANGE_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE,
    payload: { dataViewMode },
  };
}

/**
 * 表示区分（標準/出玉ランキング）を状態のみ更新する
 *
 * `changeUnitDataListReportsDataViewModeAction` と異なり、データの再取得や
 * ソート条件の退避/復元を行わない。お気に入り適用時（適用側で独自にfetchするため）のように、
 * 副作用を伴わずに表示区分を合わせたい場面でのみ使用する。
 *
 * 注意: URL→状態の同期（useUnitDataListRankingRouteSync）ではこちらではなく
 * `changeUnitDataListReportsDataViewModeAction` を使う。直リンク/リロード時は
 * 正しいエンドポイントでの再取得が必要なため、副作用の無いこちらを使うと
 * 標準/出玉ランキングのデータ不整合が起きる。
 * @param dataViewMode 表示区分
 */
function selectUnitDataListReportsDataViewModeAction(
  dataViewMode: UnitDataListDataViewMode
) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE,
    payload: { dataViewMode },
  };
}

/**
 * 表示区分をURLから確定済みとして記録する
 *
 * URL同期（useUnitDataListRankingRouteSync）は「まだURLから確定していない＝直リンク/リロード」
 * のときだけURLを正とする。この目印をコンポーネントの useRef ではなく Redux で持つのは、
 * お気に入りのクエリ（fav_id/q）が外れるとページが再マウントされ ref が初期化されるため。
 * ref のままだと、タブ操作でクエリを外した直後に直リンク扱いとなり、まだ切り替え前の
 * pathname から表示区分を戻してしまう。
 */
function selectUnitDataListReportsDataViewModeResolvedFromUrlAction() {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE_RESOLVED_FROM_URL,
    payload: {},
  };
}

/**
 * 選択したお気に入りでデータを取得する
 *
 * @param favorite 変更するお気に入りID（undefined時は選択なし）
 */
function changeUnitDataListReportsFavoriteAction(favorite?: number) {
  return {
    type: CHANGE_UNIT_DATA_LIST_REPORTS_FAVORITE,
    payload: { favorite },
  };
}

/**
 * データを取得せずにお気に入りをデフォルトに戻す
 */
function resetUnitDataListReportsFavoriteToDefaultAction() {
  return {
    type: RESET_UNIT_DATA_LIST_REPORTS_FAVORITE_TO_DEFAULT,
    payload: {},
  };
}

/**
 * 検索条件を変更
 */
function selectUnitDataListReportsSearchConditionAction(
  params: UnitDataListFormConditions
) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION,
    payload: { params },
  };
}

/**
 * 絞り込む種別を変更
 *
 * MEMO: 検索時やお気に入り適用時に使用する（全体的なStateだけ変更して最終的にFetchしたいケース）
 * @param shu 絞り込む種別
 */
export function selectUnitDataListReportsCurrentShuAction(shu?: ShuOption) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_SHU,
    payload: { shu },
  };
}

/**
 * 選択中の店舗を変更
 *
 * MEMO: 検索時やお気に入り適用時に使用する（全体的なStateだけ変更して最終的にFetchしたいケース）
 * @param halls 選択中の店舗
 */
function selectUnitDataListReportsCurrentHallsAction(halls: string[]) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS,
    payload: { halls },
  };
}

/**
 * 選択中の期間単位を変更
 * @param dateRange 期間単位
 * @param dateUnit 日付単位
 * @param isComparison 比較期間の有無
 */
function selectUnitDataListReportsDateRangeParamsAction(
  dateRange: UnitDataListSettingDateRangeParams['dateRange'],
  dateUnit: UnitDataListSettingDateRangeParams['dateUnit'],
  isComparison: UnitDataListSettingDateRangeParams['isComparison'],
  options?: { kiAlignedSlideLock: KiAlignedYmdSlideLock | undefined }
) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS,
    payload: {
      dateRange,
      dateUnit,
      isComparison,
      ...(options != null
        ? { kiAlignedSlideLock: options.kiAlignedSlideLock }
        : {}),
    },
  };
}

/**
 * 選択中の期間をデフォルトに戻す
 */
function resetUnitDataListReportsDateRangeParamsAction() {
  return {
    type: RESET_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS,
  };
}

/**
 * 検索条件をデフォルトに戻す
 */
function resetUnitDataListReportsSearchConditionAction() {
  return {
    type: RESET_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION,
  };
}

/**
 * 現在の検索条件でお気に入りを上書き保存する
 * @param name お気に入り名
 * @param isShared 共有設定
 * @param memo メモ
 * @param sharedUser 共有ユーザ
 * @param mode メモ更新モード
 */
function saveUnitDataListReportsFavoriteAction(
  name: string,
  isShared: boolean,
  memo: string,
  sharedUser: SharedUser[],
  mode?: 'standard' | 'memo'
) {
  return {
    type: SAVE_UNIT_DATA_LIST_REPORTS_FAVORITE,
    payload: { name, isShared, memo, sharedUser, mode },
  };
}

/**
 * 現在の検索条件でお気に入りを新規保存する
 * @param name お気に入り名
 * @param isShared 共有設定
 * @param memo メモ
 * @param sharedUser 共有ユーザ
 */
function saveAsUnitDataListReportsFavoriteAction(
  name: string,
  isShared: boolean,
  memo: string,
  sharedUser: SharedUser[]
) {
  return {
    type: SAVE_AS_UNIT_DATA_LIST_REPORTS_FAVORITE,
    payload: { name, isShared, memo, sharedUser },
  };
}

/**
 * テーブルの非表示項目を追加する
 * @param columnCode カラム名
 */
function addUnitDataListReportsTableFilterAction(columnCode: string) {
  return {
    type: ADD_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
    payload: { columnCode },
  };
}

/**
 * テーブルの非表示項目を削除する
 * @param columnCode カラム名
 */
function removeUnitDataListReportsTableFilterAction(columnCode: string) {
  return {
    type: REMOVE_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
    payload: { columnCode },
  };
}

/**
 * テーブルの非表示項目を反映する
 * @param tableFilterItems テーブルの非表示項目
 */
function applyUnitDataListReportsTableFilterAction(tableFilterItems: string[]) {
  return {
    type: APPLY_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
    payload: { tableFilterItems },
  };
}

/**
 * テーブルの非表示項目をリセットする
 */
function resetUnitDataListReportsTableFilterAction() {
  return {
    type: RESET_UNIT_DATA_LIST_REPORTS_TABLE_FILTER,
  };
}

/**
 * 機種名フィルタを変更
 * @param filterString 検索する文字列
 */
export function selectUnitDataListReportsModelNameFilterAction(
  filterString: string
) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_MODEL_NAME_FILTER,
    payload: { filterString },
  };
}

/**
 * タイプ列の複数選択を変更
 */
export function selectUnitDataListReportsTableHeaderSubMenuMultiFilterAction(tableHeaderSubMenuMultiFilter: {
  [columnName: string]: string[];
}) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
    payload: tableHeaderSubMenuMultiFilter,
  };
}

/**
 * タイプ列の複数選択の選択肢を変更
 */
export function selectUnitDataListReportsTableHeaderSubMenuMultiFilterSelectOptionsAction(tableHeaderSubMenuMultiFilterSelectOptions: {
  [columnName: string]: string[];
}) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS,
    payload: tableHeaderSubMenuMultiFilterSelectOptions,
  };
}

/**
 * サブメニュー内数値フィルタを変更
 * @param filterNumbers 検索する数値フィルタ条件
 */
export function selectUnitDataListReportsSubMenuNumberFilterAction(filterNumbers: {
  [field: string]: {
    minimumNumber: number | undefined;
    maximumNumber: number | undefined;
  };
}) {
  return {
    type: SELECT_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER,
    payload: { filterNumbers },
  };
}

/**
 * 数値フィルタを全てリセットする
 */
export function resetUnitDataListReportsSubMenuNumberFilterAction() {
  return {
    type: RESET_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER,
  };
}

/**
 * 期間スライドコンポーネントがクリックされた時に、期間を変更して検索を実行する
 * @param {ReportsDateRange} newDateRange   期間単位
 * @param {string} newStartDate           検索期間 開始日
 * @param {string} newEndDate             検索期間 終了日
 * @param {string} newStartComparisonDate 比較期間 開始日
 * @param {string} newEndComparisonDate   比較期間 終了日
 */
export function searchUnitDataListReportsDateRangeSlideAction(
  newDateRange: ReportsDateRange,
  newStartDate: string,
  newEndDate: string,
  newStartComparisonDate: string | undefined,
  newEndComparisonDate: string | undefined
) {
  return {
    type: SEARCH_UNIT_DATA_LIST_REPORTS_DATE_RANGE_SLIDE,
    payload: {
      dateRange: newDateRange,
      ymdList: getYmdList(newStartDate, newEndDate),
      ymdComparisonList:
        newStartComparisonDate !== undefined &&
        newEndComparisonDate !== undefined
          ? getYmdList(newStartComparisonDate, newEndComparisonDate)
          : undefined,
    },
  };
}

/**
 * 店舗選択スライダーで選択した店舗のデータを取得する
 *
 * MEMO: 初回取得時とテーブル上部の店舗選択で変更時に使用する
 * @param halls 絞り込む店舗
 */
function changeUnitDataListReportsCurrentHallsAction(halls: string[]) {
  return {
    type: CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS,
    payload: { halls },
  };
}

/**
 * 画面共有用の短縮URLを作成する
 */
function createUnitDataListReportsShortenedUrlAction(
  pageName: string,
  locationUrl: string
) {
  return {
    type: CREATE_UNIT_DATA_LIST_REPORTS_SHORTENED_URL,
    payload: { pageName, locationUrl },
  };
}

/**
 * [台別データ推移]データの検索（メインテーブルからの機種選択、機種セレクタで機種変更）
 */
function searchUnitDataListTransitionAction(
  kiCode: string,
  daiBan: number[],
  currentHall?: string[]
) {
  return {
    type: SEARCH_UNIT_DATA_LIST_TRANSITION,
    payload: { kiCode, daiBan, currentHall },
  };
}

/**
 * [台別データ推移]テーブルの検索条件を変更
 */
function selectUnitDataListTransitionSearchParamsAction(
  params: DataUnitDataListTransitionSearchParams
) {
  return {
    type: SELECT_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS,
    payload: { params },
  };
}

/**
 * [台別データ推移]テーブルのソートボタン押下
 */
function changeUnitDataListTransitionSortAction(
  sort: string,
  order: OrderType
) {
  return {
    type: CHANGE_UNIT_DATA_LIST_TRANSITION_SORT,
    payload: { sort, order },
  };
}

/**
 * [台別データ推移]テーブルの表示項目の並び順を変更
 */
function selectUnitDataListTransitionColumnsOrderAction(
  columnsOrder: string[]
) {
  return {
    type: SELECT_UNIT_DATA_LIST_TRANSITION_COLUMNS_ORDER,
    payload: { columnsOrder },
  };
}

/**
 * [台別データ推移]テーブルの表示項目の並び順を変更（columnsOrderを変更するためのラッパー）
 */
function triggerUnitDataListTransitionSwapFieldsAction(
  draggedId: string,
  droppedId: string
) {
  return {
    type: TRIGGER_UNIT_DATA_LIST_TRANSITION_SWAP_FIELDS,
    payload: { draggedId, droppedId },
  };
}

/**
 * [台別データ推移]テーブルの表示項目を変更
 */
export function changeUnitDataListTransitionFieldsAction(fields: Option[]) {
  return {
    type: CHANGE_UNIT_DATA_LIST_TRANSITION_FIELDS,
    payload: { fields },
  };
}

/**
 * [台別データ推移]テーブルの台番号を変更
 */
export function changeUnitDataListTransitionDaiBanAction(
  currentDaiBan: number[]
) {
  return {
    type: CHANGE_UNIT_DATA_LIST_TRANSITION_DAI_BAN,
    payload: { currentDaiBan },
  };
}

/**
 * [台別データ推移]テーブルの期間選択範囲を変更
 */
export function changeUnitDataListTransitionDateRangeAction(
  currentDateRange: string
) {
  return {
    type: CHANGE_UNIT_DATA_LIST_TRANSITION_DATE_RANGE,
    payload: { currentDateRange },
  };
}

/**
 * [台別データ推移]表示項目にあるソートボタン押下
 */
function triggerUnitDataListTransitionSortAction(
  sort: string,
  order: OrderType
) {
  return {
    type: TRIGGER_UNIT_DATA_LIST_TRANSITION_SORT,
    payload: { sort, order },
  };
}

/**
 * [台別データ推移]ヒートマップ表示の有効無効を変更する
 */
function selectUnitDataListTransitionIsHeatmapEnabledAction(
  isEnabled: boolean
) {
  return {
    type: SELECT_UNIT_DATA_LIST_TRANSITION_IS_HEATMAP_ENABLED,
    payload: { isEnabled },
  };
}

/**
 * [台別データ推移]テーブルのアニメーション表示の有効フラグを変更する
 */
export function selectUnitDataListTransitionIsTableAnimationEnabledAction(
  isEnabled: boolean
) {
  return {
    type: SELECT_UNIT_DATA_LIST_TRANSITION_IS_TABLE_ANIMATION_ENABLED,
    payload: { isEnabled },
  };
}

/**
 * [台別データ推移]テーブルの検索条件をリセットする
 */
function resetUnitDataListTransitionSearchParamsAction() {
  return {
    type: RESET_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS,
  };
}

/**
 * [台別データ推移]テーブルの台番を空にする
 */
export function clearUnitDataListTransitionDaiBanAction() {
  return {
    type: CLEAR_UNIT_DATA_LIST_TRANSITION_DAI_BAN,
  };
}

export const UnitDataListReportsSettingActionCreators = {
  searchUnitDataListReportsAction,
  searchResetUnitDataListReportsAction,
  triggerUnitDataListReportsSortAction,
  searchUnitDataListReportsFieldAction,
  changeUnitDataListReportsCurrentShuAction,
  changeUnitDataListReportsDataViewModeAction,
  selectUnitDataListReportsDataViewModeAction,
  selectUnitDataListReportsDataViewModeResolvedFromUrlAction,
  changeUnitDataListReportsFavoriteAction,
  selectUnitDataListReportsSearchConditionAction,
  selectUnitDataListReportsCurrentShuAction,
  selectUnitDataListReportsCurrentHallsAction,
  selectUnitDataListReportsDateRangeParamsAction,
  resetUnitDataListReportsDateRangeParamsAction,
  resetUnitDataListReportsSearchConditionAction,
  saveUnitDataListReportsFavoriteAction,
  saveAsUnitDataListReportsFavoriteAction,
  addUnitDataListReportsTableFilterAction,
  removeUnitDataListReportsTableFilterAction,
  applyUnitDataListReportsTableFilterAction,
  resetUnitDataListReportsTableFilterAction,
  selectUnitDataListReportsModelNameFilterAction,
  selectUnitDataListReportsTableHeaderSubMenuMultiFilterAction,
  selectUnitDataListReportsTableHeaderSubMenuMultiFilterSelectOptionsAction,
  selectUnitDataListReportsSubMenuNumberFilterAction,
  resetUnitDataListReportsSubMenuNumberFilterAction,
  searchUnitDataListReportsDateRangeSlideAction,
  changeUnitDataListReportsCurrentHallsAction,
  resetUnitDataListReportsFavoriteToDefaultAction,
  createUnitDataListReportsShortenedUrlAction,
  searchUnitDataListTransitionAction,
  selectUnitDataListTransitionSearchParamsAction,
  changeUnitDataListTransitionSortAction,
  selectUnitDataListTransitionColumnsOrderAction,
  changeUnitDataListTransitionFieldsAction,
  triggerUnitDataListTransitionSwapFieldsAction,
  changeUnitDataListTransitionDaiBanAction,
  changeUnitDataListTransitionDateRangeAction,
  triggerUnitDataListTransitionSortAction,
  selectUnitDataListTransitionIsHeatmapEnabledAction,
  selectUnitDataListTransitionIsTableAnimationEnabledAction,
  resetUnitDataListTransitionSearchParamsAction,
  clearUnitDataListTransitionDaiBanAction,
};

/**
 * Actions
 */

export type SearchUnitDataListReportsAction = ReturnType<
  typeof searchUnitDataListReportsAction
>;
export type SearchResetUnitDataListReportsAction = ReturnType<
  typeof searchResetUnitDataListReportsAction
>;
export type TriggerUnitDataListReportsSortAction = ReturnType<
  typeof triggerUnitDataListReportsSortAction
>;
export type SearchUnitDataListReportsFieldAction = ReturnType<
  typeof searchUnitDataListReportsFieldAction
>;

export type ChangeUnitDataListReportsCurrentShuAction = ReturnType<
  typeof changeUnitDataListReportsCurrentShuAction
>;

export type ChangeUnitDataListReportsDataViewModeAction = ReturnType<
  typeof changeUnitDataListReportsDataViewModeAction
>;

type SelectUnitDataListReportsDataViewModeAction = ReturnType<
  typeof selectUnitDataListReportsDataViewModeAction
>;

type SelectUnitDataListReportsDataViewModeResolvedFromUrlAction = ReturnType<
  typeof selectUnitDataListReportsDataViewModeResolvedFromUrlAction
>;

export type ChangeUnitDataListReportsFavoriteAction = ReturnType<
  typeof changeUnitDataListReportsFavoriteAction
>;

type SelectUnitDataListReportsSearchConditionAction = ReturnType<
  typeof selectUnitDataListReportsSearchConditionAction
>;

type SelectUnitDataListReportsCurrentShuAction = ReturnType<
  typeof selectUnitDataListReportsCurrentShuAction
>;

type SelectKiCurrentHallsAction = ReturnType<
  typeof selectUnitDataListReportsCurrentHallsAction
>;
type SelectKiDateRangeParamsAction = ReturnType<
  typeof selectUnitDataListReportsDateRangeParamsAction
>;
type ResetUnitDataListReportsDateRangeParamsAction = ReturnType<
  typeof resetUnitDataListReportsDateRangeParamsAction
>;
type ResetUnitDataListReportsSearchConditionAction = ReturnType<
  typeof resetUnitDataListReportsSearchConditionAction
>;

export type SaveUnitDataListReportsFavoriteAction = ReturnType<
  typeof saveUnitDataListReportsFavoriteAction
>;
export type SaveAsUnitDataListReportsFavoriteAction = ReturnType<
  typeof saveAsUnitDataListReportsFavoriteAction
>;

type AddUnitDataListReportsTableFilterAction = ReturnType<
  typeof addUnitDataListReportsTableFilterAction
>;
type RemoveUnitDataListReportsTableFilterAction = ReturnType<
  typeof removeUnitDataListReportsTableFilterAction
>;
type ApplyUnitDataListReportsTableFilterAction = ReturnType<
  typeof applyUnitDataListReportsTableFilterAction
>;
type ResetUnitDataListReportsTableFilterAction = ReturnType<
  typeof resetUnitDataListReportsTableFilterAction
>;
type SelectUnitDataListReportsModelNameFilterAction = ReturnType<
  typeof selectUnitDataListReportsModelNameFilterAction
>;
type SelectUnitDataListReportsTableHeaderSubMenuMultiFilterAction = ReturnType<
  typeof selectUnitDataListReportsTableHeaderSubMenuMultiFilterAction
>;
type SelectUnitDataListReportsTableHeaderSubMenuMultiFilterSelectOptionsAction =
  ReturnType<
    typeof selectUnitDataListReportsTableHeaderSubMenuMultiFilterSelectOptionsAction
  >;
type SelectUnitDataListReportsSubMenuNumberFilterAction = ReturnType<
  typeof selectUnitDataListReportsSubMenuNumberFilterAction
>;
type ResetUnitDataListReportsSubMenuNumberFilterAction = ReturnType<
  typeof resetUnitDataListReportsSubMenuNumberFilterAction
>;

export type SearchUnitDataListReportsDateRangeSlideAction = ReturnType<
  typeof searchUnitDataListReportsDateRangeSlideAction
>;

export type ChangeUnitDataListReportsCurrentHallsAction = ReturnType<
  typeof changeUnitDataListReportsCurrentHallsAction
>;

type ResetUnitDataListReportsFavoriteToDefaultAction = ReturnType<
  typeof resetUnitDataListReportsFavoriteToDefaultAction
>;

export type CreateUnitDataListReportsShortenedUrlAction = ReturnType<
  typeof createUnitDataListReportsShortenedUrlAction
>;

export type SearchUnitDataListTransitionAction = ReturnType<
  typeof searchUnitDataListTransitionAction
>;

export type SelectUnitDataListTransitionSearchParamsAction = ReturnType<
  typeof selectUnitDataListTransitionSearchParamsAction
>;

export type ChangeUnitDataListTransitionSortAction = ReturnType<
  typeof changeUnitDataListTransitionSortAction
>;

export type SelectUnitDataListTransitionColumnsOrderAction = ReturnType<
  typeof selectUnitDataListTransitionColumnsOrderAction
>;

export type ChangeUnitDataListTransitionFieldsAction = ReturnType<
  typeof changeUnitDataListTransitionFieldsAction
>;

export type TriggerUnitDataListTransitionSwapFieldsAction = ReturnType<
  typeof triggerUnitDataListTransitionSwapFieldsAction
>;

export type ChangeUnitDataListTransitionDaiBanAction = ReturnType<
  typeof changeUnitDataListTransitionDaiBanAction
>;

export type ChangeUnitDataListTransitionDateRangeAction = ReturnType<
  typeof changeUnitDataListTransitionDateRangeAction
>;

export type TriggerUnitDataListTransitionSortAction = ReturnType<
  typeof triggerUnitDataListTransitionSortAction
>;

export type SelectUnitDataListTransitionIsHeatmapEnabledAction = ReturnType<
  typeof selectUnitDataListTransitionIsHeatmapEnabledAction
>;

export type SelectUnitDataListTransitionIsTableAnimationEnabledAction =
  ReturnType<typeof selectUnitDataListTransitionIsTableAnimationEnabledAction>;

export type ResetUnitDataListTransitionSearchParamsAction = ReturnType<
  typeof resetUnitDataListTransitionSearchParamsAction
>;

export type ClearUnitDataListTransitionDaiBanAction = ReturnType<
  typeof clearUnitDataListTransitionDaiBanAction
>;

type UnitDataListReportsSettingAction =
  | SearchUnitDataListReportsAction
  | SearchResetUnitDataListReportsAction
  | ChangeUnitDataListReportsCurrentShuAction
  | ChangeUnitDataListReportsDataViewModeAction
  | SelectUnitDataListReportsDataViewModeAction
  | SelectUnitDataListReportsDataViewModeResolvedFromUrlAction
  | ChangeUnitDataListReportsFavoriteAction
  | SelectUnitDataListReportsSearchConditionAction
  | SelectUnitDataListReportsCurrentShuAction
  | SelectKiCurrentHallsAction
  | SelectKiDateRangeParamsAction
  | ResetUnitDataListReportsDateRangeParamsAction
  | ResetUnitDataListReportsSearchConditionAction
  | SaveUnitDataListReportsFavoriteAction
  | SaveAsUnitDataListReportsFavoriteAction
  | AddUnitDataListReportsTableFilterAction
  | RemoveUnitDataListReportsTableFilterAction
  | ApplyUnitDataListReportsTableFilterAction
  | ResetUnitDataListReportsTableFilterAction
  | SelectUnitDataListReportsModelNameFilterAction
  | SelectUnitDataListReportsTableHeaderSubMenuMultiFilterAction
  | SelectUnitDataListReportsTableHeaderSubMenuMultiFilterSelectOptionsAction
  | SelectUnitDataListReportsSubMenuNumberFilterAction
  | ResetUnitDataListReportsSubMenuNumberFilterAction
  | SearchUnitDataListReportsDateRangeSlideAction
  | ChangeUnitDataListReportsCurrentHallsAction
  | ResetUnitDataListReportsFavoriteToDefaultAction
  | CreateUnitDataListReportsShortenedUrlAction
  | SearchUnitDataListTransitionAction
  | SelectUnitDataListTransitionSearchParamsAction
  | ChangeUnitDataListTransitionSortAction
  | SelectUnitDataListTransitionColumnsOrderAction
  | ChangeUnitDataListTransitionFieldsAction
  | TriggerUnitDataListTransitionSwapFieldsAction
  | ChangeUnitDataListTransitionDaiBanAction
  | ChangeUnitDataListTransitionDateRangeAction
  | TriggerUnitDataListTransitionSortAction
  | SelectUnitDataListTransitionIsHeatmapEnabledAction
  | SelectUnitDataListTransitionIsTableAnimationEnabledAction
  | ResetUnitDataListTransitionSearchParamsAction
  | ClearUnitDataListTransitionDaiBanAction
  | TriggerUnitDataListReportsSortAction;

/**
 * State
 */

export type UnitDataListReportsSettingState = {
  /**
   * 現在の検索条件
   */
  searchCondition: UnitDataListFormConditions;
  /**
   * 現在選択中の種別・種別グループ（テーブルに表示されている種別・種別グループ）
   */
  currentShu?: ShuOption;
  /**
   * 表示区分（標準/出玉ランキング）
   */
  dataViewMode: UnitDataListDataViewMode;
  /**
   * 表示区分をURLから確定済みか
   * （直リンク・リロードの初期化が済んだかの目印。ページの再マウントで消えないよう
   * コンポーネントの useRef ではなくここで保持する）
   */
  isDataViewModeResolvedFromUrl: boolean;
  /**
   * 出玉ランキング表示に切り替える直前の標準表示のソート条件
   * （出玉ランキングは常に差の昇順から開始し、標準表示へ戻った際にソート条件を復元するために使用する）
   */
  standardSortSnapshot?: {
    sort: UnitDataListFormConditions['sort'];
    order: UnitDataListFormConditions['order'];
  };
  /**
   * 標準表示の表示項目（他モードへ切替時に退避し、標準へ戻った際に復元する）
   * （標準／出玉ランキングで表示項目セットが異なるため相互に持ち込まない）
   */
  standardFieldsSnapshot?: UnitDataListFormConditions['fields'];
  /**
   * 出玉ランキング表示の表示項目（標準へ切替時に退避し、出玉ランキングへ戻った際に復元する）
   * チェーン店レポート等と同様、モードごとに表示項目の選択を独立して保持する
   */
  rankingFieldsSnapshot?: UnitDataListFormConditions['fields'];
  /**
   * 現在選択中の期間指定
   */
  selectedDateRangeParams: UnitDataListSettingDateRangeParams;
  /**
   * 期間スライドで確定した今週・今月。
   * 期間を変えない再検索だけに使い、お気に入りの pageSetting には含めない。
   */
  kiAlignedSlideLock?: KiAlignedYmdSlideLock;
  /**
   * 選択中のお気に入りID
   */
  selectedFavoriteId?: number;
  /**
   * 機種名フィルタ
   */
  modelNameFilter: string;
  /**
   * タイプ列の複数選択
   */
  tableHeaderSubMenuMultiFilter: {
    [columnName: string]: string[];
  };
  /**
   * タイプ列の複数選択の選択肢
   */
  tableHeaderSubMenuMultiFilterSelectOptions: {
    [columnName: string]: string[];
  };
  /**
   * サブメニュー内数値フィルタ
   */
  subMenuNumberFilter: {
    [field: string]: {
      minimumNumber: number | undefined;
      maximumNumber: number | undefined;
    };
  };
  /**
   * テーブルの非表示項目一覧
   */
  tableFilterItems: string[];
  /**
   * テーブル上部の店舗スライダーで選択中の店舗（空配列は全店）
   */
  currentHalls: string[];
  /**
   * 台別データ推移
   */
  transitionTable: {
    searchParams: UnitDataListTransitionSearchParams;
    columnsOrder: string[];
  };
};

const initialDateRangeParams: UnitDataListSettingDateRangeParams = {
  dateUnit: dataDaiDefaultValue().dateRangeParams.dateUnit,
  dateRange: dataDaiDefaultValue().dateRangeParams.dateRange,
  isComparison: dataDaiDefaultValue().dateRangeParams.isComparison,
};

// Stateの初期値
export const initialState: UnitDataListReportsSettingState = {
  searchCondition: dataDaiDefaultValue().setting,
  currentShu: undefined,
  dataViewMode: 'unitData',
  isDataViewModeResolvedFromUrl: false,
  standardSortSnapshot: undefined,
  standardFieldsSnapshot: undefined,
  rankingFieldsSnapshot: undefined,
  selectedDateRangeParams: initialDateRangeParams,
  kiAlignedSlideLock: undefined,
  selectedFavoriteId: undefined,
  modelNameFilter: '',
  tableHeaderSubMenuMultiFilter: {},
  tableHeaderSubMenuMultiFilterSelectOptions: {},
  subMenuNumberFilter: {},
  tableFilterItems: [],
  currentHalls: [],
  transitionTable: {
    columnsOrder: [],
    searchParams: {
      startDate: '',
      endDate: '',
      daiBan: [],
      kiList: [],
      dateRange: '過去14日',
      sort: 'date',
      order: 'asc',
      isHeatmapEnabled: true,
      isTableAnimationEnabled: true,
    },
  },
};

/**
 * Selector
 */

/**
 * 台別データ一覧設定全てを取得する
 * @returns 全設定データ
 */
export function unitDataListReportsSettingSelector(rootState: {
  unitDataListReportsSetting: UnitDataListReportsSettingState;
}) {
  return rootState.unitDataListReportsSetting;
}

/**
 * 台別データ一覧の現在選択されている検索条件を取得する
 * @returns 現在選択されている検索条件
 */
export function unitDataListReportsSearchConditionSelector(rootState: {
  unitDataListReportsSetting: UnitDataListReportsSettingState;
}) {
  return rootState.unitDataListReportsSetting.searchCondition;
}

/**
 * 台別データ一覧のソート条件を取得する
 */
export const unitDataListReportsSortSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.searchCondition.sort
);

/**
 * 台別データ一覧のソート順を取得する
 */
export const unitDataListReportsOrderSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.searchCondition.order
);

/**
 * 台別データ一覧で選択中の種別・種別グループを取得する（テーブルに表示されている種別・種別グループ）
 * @returns 現在選択されている種別（undefined時未選択）
 */
export function unitDataListReportsSelectedCurrentShuSelector(rootState: {
  unitDataListReportsSetting: UnitDataListReportsSettingState;
}) {
  return rootState.unitDataListReportsSetting.currentShu;
}

/**
 * 台別データ一覧の表示区分（標準/出玉ランキング）を取得する
 * @returns 現在の表示区分
 */
export const unitDataListReportsDataViewModeSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) => unitDataListReportsSetting.dataViewMode
);

/**
 * 台別データ一覧の表示区分をURLから確定済みかを取得する
 * @returns 確定済みならtrue
 */
export const unitDataListReportsIsDataViewModeResolvedFromUrlSelector =
  createSelector(
    unitDataListReportsSettingSelector,
    (unitDataListReportsSetting) =>
      unitDataListReportsSetting.isDataViewModeResolvedFromUrl
  );

/**
 * 台別データ一覧で選択中の期間指定を取得する
 * @returns 選択中の機期間情報
 */
export function unitDataListReportsSelectedDateRangeParamsSelector(rootState: {
  unitDataListReportsSetting: UnitDataListReportsSettingState;
}) {
  return rootState.unitDataListReportsSetting.selectedDateRangeParams;
}

/**
 * スライドで確定した今週・今月。未確定のときは undefined。
 */
export function unitDataListReportsKiAlignedSlideLockSelector(rootState: {
  unitDataListReportsSetting: UnitDataListReportsSettingState;
}) {
  return rootState.unitDataListReportsSetting.kiAlignedSlideLock;
}

/**
 * 検索条件に指定している店舗
 */
export const unitDataListReportsSelectedHallsSelector = createSelector(
  unitDataListReportsSearchConditionSelector,
  (searchCondition) => searchCondition.halls ?? []
);

/**
 * 店舗スライダーで選択中の店舗（空配列は全店）
 */
export const unitDataListReportsCurrentHallsSelector = createSelector(
  unitDataListReportsSettingSelector,
  ({ currentHalls }) => (currentHalls.length > 0 ? currentHalls : [])
);

/**
 * 台別データ一覧で選択中のお気に入りIDを取得する
 * @returns 現在選択中のお気に入りID（undefined時未選択）
 */
export const unitDataListReportsSelectedFavoriteSelector = (
  state: RootState
) => {
  return state.unitDataListReportsSetting.selectedFavoriteId;
};

/**
 * 台別データ一覧で現在選択中のお気に入りデータを取得する
 * @returns 現在選択中のお気に入りデータ（undefined時未選択）
 */
export const unitDataListReportsSelectedFavoriteDataSelector = createSelector(
  [unitDataListReportsSelectedFavoriteSelector, favoritesSelector],
  (favoriteId, favorites) => {
    if (favoriteId === undefined) return;

    return favorites?.favorites?.find((favorite) => favorite.id === favoriteId);
  }
);

/**
 * 台別データ一覧で選択中のお気に入りデータを取得する
 * @returns 現在選択中のお気に入りデータ（undefined時未選択）
 */
export const unitDataListReportsSelectedFavoriteSettingSelector =
  createSelector(
    [unitDataListReportsSelectedFavoriteSelector, favoritesSelector],
    (favoriteId, favorites) => {
      if (favoriteId === undefined) return;

      const favoriteItem = favorites?.favorites?.find(
        (favorite) => favorite.id === favoriteId
      );

      return favoriteItem?.pageSetting?.unitDataListReports;
    }
  );

/**
 * 台別で入力されている機種名フィルタを返す
 */
export const unitDataListReportsModelNameFilterSelector = (rootState: {
  unitDataListReportsSetting: UnitDataListReportsSettingState;
}) => rootState.unitDataListReportsSetting.modelNameFilter;

/**
 * 台別データで選択されているタイプ列の複数選択を返す
 */
export const unitDataListReportsTableHeaderSubMenuMultiFilterSelector =
  (rootState: {
    unitDataListReportsSetting: UnitDataListReportsSettingState;
  }) => rootState.unitDataListReportsSetting.tableHeaderSubMenuMultiFilter;

/**
 * 台別データのタイプ列の複数選択の選択肢を返す
 */
export const unitDataListReportsTableHeaderSubMenuMultiFilterSelectOptionsSelector =
  (rootState: {
    unitDataListReportsSetting: UnitDataListReportsSettingState;
  }) =>
    rootState.unitDataListReportsSetting
      .tableHeaderSubMenuMultiFilterSelectOptions;

/**
 * 台別データで入力されているサブメニュー内の数値フィルタを返す
 */
export const unitDataListReportsSubMenuNumberFilterSelector = (rootState: {
  unitDataListReportsSetting: UnitDataListReportsSettingState;
}) => rootState.unitDataListReportsSetting.subMenuNumberFilter;

/**
 * 非表示項目一覧を取得する
 * @returns 非表示項目一覧
 */
export const unitDataListReportsSelectedTableFilterSelector = (
  state: RootState
) => {
  return state.unitDataListReportsSetting.tableFilterItems;
};

/**
 * [台別データ推移]テーブルの列順を取得する
 */
export const unitDataListTransitionColumnsOrderSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.columnsOrder
);

/**
 * [台別データ推移]テーブルの検索条件を取得する
 */
export const unitDataListTransitionSearchParamsSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.searchParams
);

/**
 * [台別データ推移]テーブルの表示項目を取得する
 */
export const unitDataListTransitionFieldsSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.searchParams.fields
);

/**
 * [台別データ推移]テーブルの台番号を取得する
 */
export const unitDataListTransitionDaiBanSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.searchParams.daiBan
);

/**
 * [台別データ推移]テーブルの期間選択範囲を取得する
 */
export const unitDataListTransitionDateRangeSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.searchParams.dateRange
);

/**
 * [台別データ推移]テーブルのソートを取得する
 */
export const unitDataListTransitionSortSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.searchParams.sort
);

/**
 * [台別データ推移]テーブルのorderを取得する
 */
export const unitDataListTransitionOrderSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.searchParams.order
);

/**
 * [台別データ推移]ヒートマップ表示の有効無効を取得
 */
export const UnitDataListTransitionReportsSettingIsHeatmapEnabledSelector =
  createSelector(
    unitDataListReportsSettingSelector,
    (unitDataListReportsSetting) =>
      unitDataListReportsSetting.transitionTable.searchParams.isHeatmapEnabled
  );

/**
 * [台別データ推移] kiListを取得
 */
export const UnitDataListTransitionKiListSelector = createSelector(
  unitDataListReportsSettingSelector,
  (unitDataListReportsSetting) =>
    unitDataListReportsSetting.transitionTable.searchParams.kiList
);

/**
 * [台別データ推移] アニメーション表示の有効無効を取得
 */
export const UnitDataListTransitionReportsSettingIsTableAnimationEnabledSelector =
  createSelector(
    unitDataListReportsSettingSelector,
    (unitDataListReportsSetting) =>
      unitDataListReportsSetting.transitionTable.searchParams
        .isTableAnimationEnabled
  );

/**
 * Reducer
 */

export function unitDataListReportsSettingReducer(
  state = initialState,
  action: UnitDataListReportsSettingAction
): UnitDataListReportsSettingState {
  switch (action.type) {
    case SELECT_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION:
      return {
        ...state,
        searchCondition: action.payload.params,
      };
    case RESET_UNIT_DATA_LIST_REPORTS_SEARCH_CONDITION:
      return {
        ...state,
        searchCondition: initialState.searchCondition,
      };
    case SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_SHU:
    case CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_SHU:
      return {
        ...state,
        currentShu: action.payload.shu,
      };
    case CHANGE_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE: {
      const nextDataViewMode = action.payload.dataViewMode;
      if (nextDataViewMode === 'ranking') {
        // 標準 → 出玉ランキングへの切替時：
        // - 現在（標準）のソート条件を退避し、常に差の昇順から開始する
        // - 現在（標準）の表示項目を退避し、出玉ランキングは前回の項目（無ければサーバー既定）を復元する
        //   （標準／出玉ランキングで表示項目セットが異なるため、モードごとに独立して保持する）
        return {
          ...state,
          dataViewMode: nextDataViewMode,
          standardSortSnapshot: {
            sort: state.searchCondition.sort,
            order: state.searchCondition.order,
          },
          standardFieldsSnapshot: state.searchCondition.fields,
          searchCondition: {
            ...state.searchCondition,
            sort: DTMA_RANKING_DEFAULT_SORT,
            order: DTMA_RANKING_DEFAULT_ORDER,
            fields: state.rankingFieldsSnapshot,
          },
        };
      }
      // 出玉ランキング → 標準表示への切替時：
      // - 退避があれば標準のソートを復元、無ければ標準の既定に戻す
      //   （お気に入りで直接ランキングに入った場合など、退避が無い時に
      //   ランキングのソートを標準へ持ち込まないようにする）
      // - 現在（出玉ランキング）の表示項目を退避し、標準の表示項目を復元する
      return {
        ...state,
        dataViewMode: nextDataViewMode,
        standardSortSnapshot: undefined,
        rankingFieldsSnapshot: state.searchCondition.fields,
        searchCondition: {
          ...state.searchCondition,
          sort: state.standardSortSnapshot
            ? state.standardSortSnapshot.sort
            : initialState.searchCondition.sort,
          order: state.standardSortSnapshot
            ? state.standardSortSnapshot.order
            : initialState.searchCondition.order,
          // 退避しておいた標準の表示項目を復元する（未退避=undefinedなら標準の既定）
          fields: state.standardFieldsSnapshot,
        },
      };
    }
    case SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE:
      // 表示区分のみを更新する（ソート/表示項目の退避・復元・再取得は行わない）
      // お気に入り適用・リセットの直後に別アクションでsearchConditionを明示的に
      // 設定するため、モードごとの退避内容はここで無条件にリセットする
      // （退避を残すと、リセットしたはずの他モードのカスタマイズが復活してしまう）
      return {
        ...state,
        dataViewMode: action.payload.dataViewMode,
        standardSortSnapshot: undefined,
        standardFieldsSnapshot: undefined,
        rankingFieldsSnapshot: undefined,
      };
    case SELECT_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE_RESOLVED_FROM_URL:
      // 一度確定したら以降は変わらないため、同値のときは参照を保って再レンダリングを避ける
      if (state.isDataViewModeResolvedFromUrl) {
        return state;
      }
      return {
        ...state,
        isDataViewModeResolvedFromUrl: true,
      };
    case SELECT_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS: {
      const dateUnit = action.payload.dateUnit;
      const dateRange = action.payload.dateRange;
      const isComparison = action.payload.isComparison;
      const hasSlideLockUpdate = 'kiAlignedSlideLock' in action.payload;
      return {
        ...state,
        selectedDateRangeParams: {
          dateUnit,
          dateRange,
          isComparison,
        },
        ...(hasSlideLockUpdate
          ? { kiAlignedSlideLock: action.payload.kiAlignedSlideLock }
          : {}),
      };
    }
    case SELECT_UNIT_DATA_LIST_REPORTS_MODEL_NAME_FILTER:
      return {
        ...state,
        modelNameFilter: action.payload.filterString,
      };
    case SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER:
      return {
        ...state,
        tableHeaderSubMenuMultiFilter: action.payload,
      };
    case SELECT_UNIT_DATA_LIST_REPORTS_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS:
      return {
        ...state,
        tableHeaderSubMenuMultiFilterSelectOptions: action.payload,
      };
    case SELECT_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER:
      return {
        ...state,
        subMenuNumberFilter: {
          ...state.subMenuNumberFilter,
          ...action.payload.filterNumbers,
        },
      };
    case RESET_UNIT_DATA_LIST_REPORTS_SUB_MENU_NUMBER_FILTER:
      return {
        ...state,
        subMenuNumberFilter: {},
      };

    case RESET_UNIT_DATA_LIST_REPORTS_DATE_RANGE_PARAMS:
      return {
        ...state,
        selectedDateRangeParams: initialState.selectedDateRangeParams,
        kiAlignedSlideLock: undefined,
      };
    case CHANGE_UNIT_DATA_LIST_REPORTS_FAVORITE:
      return {
        ...state,
        selectedFavoriteId: action.payload.favorite,
      };
    case ADD_UNIT_DATA_LIST_REPORTS_TABLE_FILTER:
      return {
        ...state,
        tableFilterItems: [
          ...state.tableFilterItems,
          action.payload.columnCode,
        ],
      };
    case REMOVE_UNIT_DATA_LIST_REPORTS_TABLE_FILTER:
      return {
        ...state,
        tableFilterItems: state.tableFilterItems.filter(
          (columnCode) => columnCode !== action.payload.columnCode
        ),
      };
    case APPLY_UNIT_DATA_LIST_REPORTS_TABLE_FILTER:
      return {
        ...state,
        tableFilterItems: action.payload.tableFilterItems,
      };
    case RESET_UNIT_DATA_LIST_REPORTS_TABLE_FILTER:
      return {
        ...state,
        tableFilterItems: [],
      };
    case RESET_UNIT_DATA_LIST_REPORTS_FAVORITE_TO_DEFAULT:
      return {
        ...state,
        selectedFavoriteId: undefined,
      };
    case SELECT_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS:
    case CHANGE_UNIT_DATA_LIST_REPORTS_CURRENT_HALLS:
      return {
        ...state,
        currentHalls: action.payload.halls,
      };
    case SELECT_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          searchParams: {
            ...state.transitionTable.searchParams,
            ...action.payload.params,
          },
        },
      };
    case SELECT_UNIT_DATA_LIST_TRANSITION_COLUMNS_ORDER:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          columnsOrder: action.payload.columnsOrder,
        },
      };
    case CHANGE_UNIT_DATA_LIST_TRANSITION_DATE_RANGE:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          searchParams: {
            ...state.transitionTable.searchParams,
            dateRange: action.payload.currentDateRange,
          },
        },
      };
    case TRIGGER_UNIT_DATA_LIST_TRANSITION_SORT:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          searchParams: {
            ...state.transitionTable.searchParams,
            sort: action.payload.sort,
            order: action.payload.order,
          },
        },
      };
    case TRIGGER_UNIT_DATA_LIST_REPORTS_SORT:
      return {
        ...state,
        searchCondition: {
          ...state.searchCondition,
          sort: action.payload.sort,
          order: action.payload.order,
        },
      };
    case SELECT_UNIT_DATA_LIST_TRANSITION_IS_HEATMAP_ENABLED:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          searchParams: {
            ...state.transitionTable.searchParams,
            isHeatmapEnabled: action.payload.isEnabled,
          },
        },
      };
    case SELECT_UNIT_DATA_LIST_TRANSITION_IS_TABLE_ANIMATION_ENABLED:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          searchParams: {
            ...state.transitionTable.searchParams,
            isTableAnimationEnabled: action.payload.isEnabled,
          },
        },
      };
    case RESET_UNIT_DATA_LIST_TRANSITION_SEARCH_PARAMS:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          searchParams: initialState.transitionTable.searchParams,
        },
      };
    case CLEAR_UNIT_DATA_LIST_TRANSITION_DAI_BAN:
      return {
        ...state,
        transitionTable: {
          ...state.transitionTable,
          searchParams: {
            ...state.transitionTable.searchParams,
            daiBan: [],
          },
        },
      };
    default:
      return state;
  }
}
